import type { ComponentType } from 'react';
import { t } from '@shared/i18n';
import {
  ActivityCreatedGroupIcon,
  ActivityEditIcon,
  ActivityUpdateIcon,
  ActivityAddMemberIcon,
  ActivityPaidIcon,
  ActivityRemoveMemberIcon,
  ActivityCloseIcon,
  ActivityDeleteIcon,
  ActivityEditExpenseIcon,
  ActivitySetByIcon,
  ActivityArchivedIcon,
  ActivityReopenedIcon,
  ActivityUpdatedIcon,
  ActivityRestoredIcon,
  ActivityAddedIcon,
  ActivityExpenseAddedIcon,
} from '@ui/icons';

export type ActivityType =
  | 'createdGroup'
  | 'edit'
  | 'update'
  | 'addMember'
  | 'paid'
  | 'removeMember'
  | 'close'
  | 'delete'
  | 'editExpense'
  | 'setBy'
  | 'archived'
  | 'unarchived'
  | 'reopened'
  | 'updated'
  | 'restored'
  | 'added'
  | 'expenseAdded';

export interface ActivityMeta {
  activityType: ActivityType;
  icon: ComponentType<{ className?: string }>;
  className: string;
}

function normalizeModule(mod?: string | null): string {
  if (!mod) return '';
  const clean = mod.toLowerCase().replace(/[-_]/g, '');
  if (clean === 'groups' || clean === 'group') return 'groups';
  if (
    clean === 'expenses' ||
    clean === 'expense' ||
    clean === 'pendingexpenses' ||
    clean === 'pendingexpense'
  )
    return 'expenses';
  if (clean === 'expensepayments' || clean === 'expensepayment') return 'expensePayments';
  if (clean === 'expensesplits' || clean === 'expensesplit') return 'expenseSplits';
  if (clean === 'expensesettlements' || clean === 'expensesettlement') return 'expenseSettlements';
  if (clean === 'members' || clean === 'member') return 'members';
  if (clean === 'wallet') return 'wallet';
  if (clean === 'walletdeposit') return 'walletDeposit';
  if (clean === 'walletthreshold') return 'walletThreshold';
  if (clean === 'settlements' || clean === 'settlement' || clean === 'settle') return 'settlements';
  return clean;
}

function normalizeAction(action?: string | null): string {
  if (!action) return '';
  const clean = action.toLowerCase().replace(/[-_]/g, '');
  if (clean === 'created' || clean === 'create') return 'created';
  if (clean === 'updated' || clean === 'update' || clean === 'edited' || clean === 'edit')
    return 'updated';
  if (clean === 'deleted' || clean === 'delete' || clean === 'removed' || clean === 'remove')
    return 'deleted';
  if (clean === 'closed' || clean === 'close') return 'closed';
  if (clean === 'archived' || clean === 'archive') return 'archived';
  if (clean === 'restored' || clean === 'restore') return 'restored';
  if (clean === 'reopened' || clean === 'reopen') return 'reopened';
  if (clean === 'unarchived' || clean === 'unarchive') return 'unarchived';
  if (clean === 'added' || clean === 'add') return 'added';
  return clean;
}

/**
 * Maps reference module and action type to mobile ActivityType enum.
 */
export function getActivityType(
  referenceModule?: string | null,
  actionType?: string | null,
): ActivityType {
  const mod = normalizeModule(referenceModule);
  const act = normalizeAction(actionType);

  switch (mod) {
    /// ---------- GROUP ----------
    case 'groups':
      switch (act) {
        case 'created':
          return 'createdGroup';
        case 'updated':
          return 'update';
        case 'deleted':
          return 'delete';
        case 'closed':
          return 'close';
        case 'archived':
          return 'archived';
        case 'restored':
        case 'reopened':
          return 'restored';
        case 'unarchived':
          return 'unarchived';
        default:
          return 'createdGroup';
      }

    /// ---------- EXPENSE ----------
    case 'expenses':
      switch (act) {
        case 'created':
        case 'added':
          return 'expenseAdded';
        case 'updated':
          return 'updated';
        case 'deleted':
          return 'delete';
        case 'restored':
          return 'restored';
        default:
          return 'expenseAdded';
      }

    /// ---------- EXPENSE PAYMENT ----------
    case 'expensePayments':
      switch (act) {
        case 'created':
        case 'added':
          return 'added';
        case 'updated':
          return 'updated';
        case 'deleted':
          return 'delete';
        default:
          return 'added';
      }

    /// ---------- EXPENSE SPLIT ----------
    case 'expenseSplits':
      switch (act) {
        case 'created':
        case 'added':
          return 'added';
        case 'updated':
          return 'updated';
        case 'deleted':
          return 'delete';
        default:
          return 'added';
      }

    /// ---------- SETTLEMENT (EXPENSE SETTLEMENTS) ----------
    case 'expenseSettlements':
      switch (act) {
        case 'created':
        case 'added':
          return 'added';
        case 'updated':
          return 'updated';
        case 'deleted':
          return 'delete';
        default:
          return 'added';
      }

    /// ---------- MEMBERS ----------
    case 'members':
      switch (act) {
        case 'created':
        case 'added':
          return 'addMember';
        case 'deleted':
          return 'removeMember';
        case 'updated':
          return 'addMember';
        default:
          return 'addMember';
      }

    /// ---------- WALLET ----------
    case 'wallet':
      switch (act) {
        case 'created':
        case 'added':
          return 'added';
        case 'updated':
          return 'update';
        case 'deleted':
          return 'delete';
        case 'closed':
          return 'close';
        case 'restored':
        case 'reopened':
          return 'reopened';
        default:
          return 'added';
      }

    /// ---------- WALLET DEPOSIT ----------
    case 'walletDeposit':
      switch (act) {
        case 'created':
        case 'added':
          return 'expenseAdded';
        case 'updated':
          return 'update';
        case 'deleted':
          return 'delete';
        case 'closed':
          return 'close';
        case 'restored':
        case 'reopened':
          return 'reopened';
        default:
          return 'added';
      }

    /// ---------- WALLET THRESHOLD ----------
    case 'walletThreshold':
      switch (act) {
        case 'created':
        case 'added':
          return 'setBy';
        case 'updated':
          return 'update';
        default:
          return 'added';
      }

    /// ---------- SETTLEMENT ----------
    case 'settlements':
      switch (act) {
        case 'created':
        case 'added':
          return 'paid';
        case 'updated':
          return 'update';
        case 'deleted':
          return 'delete';
        case 'closed':
          return 'close';
        case 'restored':
        case 'reopened':
          return 'reopened';
        default:
          return 'paid';
      }

    default:
      switch (act) {
        case 'created':
        case 'added':
          return 'added';
        case 'updated':
          return 'update';
        case 'deleted':
          return 'delete';
        case 'closed':
          return 'close';
        case 'archived':
          return 'archived';
        case 'restored':
          return 'restored';
        case 'reopened':
          return 'reopened';
        default:
          return 'added';
      }
  }
}

const ACTIVITY_META_MAP: Record<
  ActivityType,
  { icon: ComponentType<{ className?: string }>; className: string }
> = {
  createdGroup: { icon: ActivityCreatedGroupIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  edit: { icon: ActivityEditIcon, className: 'bg-[#D4EAFC] text-[#008BFF]' },
  update: { icon: ActivityUpdateIcon, className: 'bg-[#D4EAFC] text-[#008BFF]' },
  addMember: { icon: ActivityAddMemberIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  paid: { icon: ActivityPaidIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  removeMember: { icon: ActivityRemoveMemberIcon, className: 'bg-[#FFDDF5] text-[#B24591]' },
  close: { icon: ActivityCloseIcon, className: 'bg-[#FFDEDF] text-[#D74949]' },
  delete: { icon: ActivityDeleteIcon, className: 'bg-[#FFDEDF] text-[#D74949]' },
  editExpense: { icon: ActivityEditExpenseIcon, className: 'bg-[#D4EAFC] text-[#008BFF]' },
  setBy: { icon: ActivitySetByIcon, className: 'bg-[#D4EAFC] text-[#008BFF]' },
  archived: { icon: ActivityArchivedIcon, className: 'bg-[#FFDDF5] text-[#B24591]' },
  reopened: { icon: ActivityReopenedIcon, className: 'bg-[#D4EAFC] text-[#008BFF]' },
  updated: { icon: ActivityUpdatedIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  restored: { icon: ActivityRestoredIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  unarchived: { icon: ActivityRestoredIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  added: { icon: ActivityAddedIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
  expenseAdded: { icon: ActivityExpenseAddedIcon, className: 'bg-[#D5F1E1] text-[#4A9167]' },
};

export function getActivityMeta(
  referenceModule?: string | null,
  actionType?: string | null,
): ActivityMeta {
  const type = getActivityType(referenceModule, actionType);
  const meta = ACTIVITY_META_MAP[type];
  return {
    activityType: type,
    icon: meta.icon,
    className: meta.className,
  };
}

/**
 * Maps reference module and action type to its display label ("Created", "Added", "Updated", "Deleted", "Removed", "Enabled", "Set", etc.)
 * matching mobile logic.
 */
export function getActivityTypeLabel(
  referenceModule?: string | null,
  actionType?: string | null,
): string {
  const mod = normalizeModule(referenceModule);
  const act = normalizeAction(actionType);

  switch (mod) {
    /// ---------------- GROUP ----------------
    case 'groups':
      switch (act) {
        case 'created':
          return t.recentActivity.types.created;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        case 'closed':
          return t.recentActivity.types.closed;
        case 'restored':
        case 'unarchived':
          return t.recentActivity.types.restored;
        case 'archived':
          return t.recentActivity.types.archived;
        case 'reopened':
          return t.recentActivity.types.reopened;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- EXPENSES ----------------
    case 'expenses':
    case 'expensePayments':
    case 'expenseSplits':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.added;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        case 'restored':
          return t.recentActivity.types.restored;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- SETTLEMENTS ----------------
    case 'expenseSettlements':
    case 'settlements':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.added;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- MEMBERS ----------------
    case 'members':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.added;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
        case 'removed':
          return t.recentActivity.types.removed;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- WALLET ----------------
    case 'wallet':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.enabled;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        case 'closed':
          return t.recentActivity.types.closed;
        case 'restored':
          return t.recentActivity.types.restored;
        case 'reopened':
          return t.recentActivity.types.reopened;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- WALLET DEPOSIT ----------------
    case 'walletDeposit':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.added;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        case 'closed':
          return t.recentActivity.types.closed;
        case 'restored':
          return t.recentActivity.types.restored;
        case 'reopened':
          return t.recentActivity.types.reopened;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    /// ---------------- WALLET THRESHOLD ----------------
    case 'walletThreshold':
      switch (act) {
        case 'created':
        case 'added':
          return t.recentActivity.types.set;
        case 'updated':
          return t.recentActivity.types.updated;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }

    default:
      switch (act) {
        case 'created':
          return t.recentActivity.types.created;
        case 'added':
          return t.recentActivity.types.added;
        case 'updated':
          return t.recentActivity.types.updated;
        case 'deleted':
          return t.recentActivity.types.deleted;
        case 'closed':
          return t.recentActivity.types.closed;
        case 'archived':
          return t.recentActivity.types.archived;
        case 'restored':
        case 'unarchived':
          return t.recentActivity.types.restored;
        case 'reopened':
          return t.recentActivity.types.reopened;
        case 'removed':
          return t.recentActivity.types.removed;
        default:
          return act ? act.charAt(0).toUpperCase() + act.slice(1) : t.recentActivity.types.other;
      }
  }
}
